import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { rollupIdenticalWindows } from '../utils/rollup-windows';

const BROWN     = [139, 69, 19];
const BROWN_L   = [200, 160, 110];
const TEAL      = [0, 105, 92];
const TEAL_BG   = [224, 247, 242];
const TEAL_TEXT = [0, 77, 64];
const WHITE     = [255, 255, 255];
const LIGHT     = [240, 240, 240];
const DARK      = [40, 40, 40];
const LABEL     = [100, 60, 20];

const PAGE_W = 279.4;
const PAGE_H = 215.9;
const MARGIN = 10;
const HDR_H  = 38;
const FTR_H  = 10;

const LOGO_URL = '/brand/twp-logo.png';
let _logo = null;
async function getLogo() {
  if (_logo) return _logo;
  try {
    const res = await fetch(LOGO_URL);
    const blob = await res.blob();
    return new Promise(r => {
      const fr = new FileReader();
      fr.onloadend = () => { _logo = fr.result; r(_logo); };
      fr.onerror = () => r(null);
      fr.readAsDataURL(blob);
    });
  } catch { return null; }
}

function fmtDate(val) {
  if (!val) return new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  if (val?.toDate) return val.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  if (typeof val === 'string' && val.includes('-')) {
    const [y, m, d] = val.split('-');
    return new Date(+y, +m - 1, +d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
  return String(val);
}

function v(val) { return val && String(val).trim() ? String(val).trim() : ''; }

function drawHeader(doc, jo, logo, pageNum, totalPages) {
  doc.setFillColor(...WHITE);
  doc.rect(0, 0, PAGE_W, HDR_H, 'F');
  doc.setFillColor(...TEAL);
  doc.rect(0, 0, 3, HDR_H, 'F');
  doc.setDrawColor(...BROWN_L);
  doc.setLineWidth(0.6);
  doc.line(0, HDR_H, PAGE_W, HDR_H);

  if (logo) { try { doc.addImage(logo, 'PNG', MARGIN + 4, 5, 16, 16); } catch {} }

  doc.setTextColor(...BROWN);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('The Window Place LLC', MARGIN + 24, 12);
  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...LABEL);
  doc.text('1300 E. 4th Ave  •  Milbank, South Dakota 57252', MARGIN + 24, 17);
  doc.text('(605) 432-1834  •  Toll Free (877) 224-0745  •  Fax (866) 432-1718', MARGIN + 24, 21.5);

  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...TEAL);
  doc.text('ORDERED PRODUCTS', PAGE_W - MARGIN, 13, { align: 'right' });
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BROWN);
  doc.text(`Contract #${jo.contractNumber}`, PAGE_W - MARGIN, 21, { align: 'right' });

  const c = jo.customer || {};
  const fields = [
    ['Name',       c.name       || ''],
    ['Address',    c.address    || ''],
    ['City',       c.city       || ''],
    ['State',      c.state      || ''],
    ['Zip',        c.zip        || ''],
    ['Home Ph',    c.phone      || ''],
    ['Cell Ph',    c.cellPhone  || ''],
    ['Work Ph',    c.workPhone  || ''],
    ['Order Date', fmtDate(jo.orderDate || jo.createdAt)],
    ['Page',       `${pageNum} of ${totalPages}`],
  ];
  let fx = MARGIN + 4;
  fields.forEach(([label, value]) => {
    doc.setFontSize(5.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...LABEL);
    doc.text(label + ':', fx, 28);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...DARK);
    doc.setFontSize(6.5);
    doc.text(String(value), fx, 33);
    fx += 26;
  });
}

function drawFooter(doc, jo, pageNum, totalPages) {
  const y = PAGE_H - FTR_H;
  doc.setFillColor(...WHITE);
  doc.rect(0, y, PAGE_W, FTR_H, 'F');
  doc.setFillColor(...TEAL);
  doc.rect(0, y, 3, FTR_H, 'F');
  doc.setDrawColor(...BROWN_L);
  doc.setLineWidth(0.6);
  doc.line(0, y, PAGE_W, y);
  const now = new Date();
  const printed = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    + ' ' + now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  doc.setTextColor(...LABEL);
  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'normal');
  doc.text(
    `Contract #${jo.contractNumber}  •  ${jo.customer?.name || ''}  •  Ordered Products Shop Order  •  Printed: ${printed}  •  Page ${pageNum} of ${totalPages}`,
    PAGE_W / 2, y + 6.5, { align: 'center' }
  );
}

function buildHead() {
  return [[
    'Qty',
    'Description / Type',
    'Width x Height',
    'UI',
    'Outside Color',
    'Inside Color',
    'Room',
    'Notes / Special Requirements',
  ]];
}

function buildBody(items) {
  return items.map(w => {
    const ww    = parseFloat(w.width)  || 0;
    const hh    = parseFloat(w.height) || 0;
    const sizeS = ww && hh ? `${w.width}" x ${w.height}"` : (w.width ? `${w.width}"` : w.height ? `${w.height}"` : '');
    const uiV   = ww && hh ? String(ww + hh) : '';

    return [
      { content: w.qty || 1, styles: { halign: 'center', fontStyle: 'bold' } },
      v(w.style || w.description),
      { content: sizeS, styles: { halign: 'center' } },
      { content: uiV,   styles: { halign: 'center' } },
      { content: v(w.colorOutside), styles: { halign: 'center' } },
      { content: v(w.colorInside),  styles: { halign: 'center' } },
      { content: v(w.room),         styles: { halign: 'center' } },
      v(w.extrasNotes || ''),
    ];
  });
}

const TABLE_STYLES = {
  styles: {
    fontSize: 7,
    cellPadding: { top: 2.5, bottom: 2.5, left: 2, right: 2 },
    textColor: DARK,
    lineColor: LIGHT,
    lineWidth: 0.15,
    overflow: 'linebreak',
    valign: 'middle',
  },
  headStyles: {
    fillColor: TEAL_BG,
    textColor: TEAL_TEXT,
    fontStyle: 'bold',
    fontSize: 6,
    halign: 'center',
    valign: 'middle',
    minCellHeight: 10,
  },
  alternateRowStyles: { fillColor: [245, 251, 249] },
  columnStyles: {
    0: { cellWidth: 8    },
    1: { cellWidth: 54   },
    2: { cellWidth: 22   },
    3: { cellWidth: 12   },
    4: { cellWidth: 20   },
    5: { cellWidth: 20   },
    6: { cellWidth: 16   },
    7: { cellWidth: 42   },
    8: { cellWidth: 'auto' },
  },
  margin: { left: MARGIN, right: MARGIN, bottom: FTR_H + 8 },
};

export async function generateOrderedWindowsShopPDF(jo, items, returnBlob = false) {
  const logo = await getLogo();
  const head = buildHead();
  // Collapse identical mulled-pane rows into single rows with summed qty
  const rolledItems = rollupIdenticalWindows(items);
  const body = buildBody(rolledItems);

  const docCount = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'letter' });
  let totalPages = 1;
  autoTable(docCount, {
    startY: HDR_H + 4, head, body, ...TABLE_STYLES,
    didDrawPage: (data) => { totalPages = data.pageNumber; },
  });

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'letter' });
  drawHeader(doc, jo, logo, 1, totalPages);
  drawFooter(doc, jo, 1, totalPages);

  autoTable(doc, {
    startY: HDR_H + 4, head, body, ...TABLE_STYLES,
    didDrawPage: (data) => {
      if (data.pageNumber > 1) {
        drawHeader(doc, jo, logo, data.pageNumber, totalPages);
        drawFooter(doc, jo, data.pageNumber, totalPages);
      }
    },
  });

  const finalY = doc.lastAutoTable.finalY + 6;
  if (finalY < PAGE_H - FTR_H - 8) {
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...TEAL_TEXT);
    doc.text(
      `${items.length} ordered item${items.length !== 1 ? 's' : ''} on this shop order  •  Attach to package in shop`,
      MARGIN, finalY
    );
  }

  const safeName = (jo.customer?.name || 'Customer').replace(/[^a-zA-Z0-9 ]/g, '').trim();
  if (returnBlob) return doc.output('blob');
  doc.save(`ShopOrder_Ordered_${jo.contractNumber}_${safeName}.pdf`);
}